import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { PreviewBanner } from "@/components/sci/PreviewBanner";
import { QSection } from "@/components/sci/subject/Bits";
import { useSciPreview } from "@/components/sci/useSciPreview";
import { SciNotFound as NotFound, SciLoadError } from "@/components/sci/SciNotFound";
import { GraduatePanel } from "@/components/sci/intl/GraduatePanel";
import { OccupationPanel } from "@/components/sci/intl/OccupationPanel";
import { LoanPanel } from "@/components/sci/intl/LoanPanel";
import { CostsPanel } from "@/components/sci/intl/CostsPanel";
import { CountryCoverage, Definitions, SourcesQa } from "@/components/sci/intl/Defs";

export default function SciIntlPreview() {
  const { slug } = useParams();
  const { loading, data: d, missing, error, retry } = useSciPreview(`/sci/preview/subjects/${slug}/international`);
  if (loading) return <div className="min-h-[60vh]" data-testid="sci-intl-loading" />;
  if (missing) return <NotFound />;
  if (error) return <SciLoadError onRetry={retry} />;
  const name = d.subject.name;
  return (
    <div className="bg-[#FDFBF7]" data-testid="sci-intl-page">
      <Helmet><meta name="robots" content="noindex,nofollow" /><title>{`${name}: international evidence · private preview`}</title></Helmet>
      <PreviewBanner runId={`${d._schema} · built ${d.built_at.slice(0, 10)}`} />
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="pt-10">
          <Link to={`/preview/sci/subjects/${slug}`} data-testid="sci-intl-back" className="text-xs text-stone-500 underline underline-offset-2 hover:text-[#002147]">Back to the {name} guide</Link>
          <p className="mt-6 font-mono text-xs uppercase tracking-[0.2em] text-[#92400E]">International evidence · Phases 1–2 · admin preview</p>
          <h1 data-testid="sci-intl-title" className="mt-3 font-editorial text-4xl text-stone-900 sm:text-5xl lg:text-6xl">{name} beyond the UK</h1>
          <p className="mt-4 max-w-3xl text-sm text-stone-600 md:text-base">
            Official tuition and living-cost figures for eight countries, plus earnings, occupation pay and student-loan rules for the UK and US. Each figure shows who it applies to and its period; sources and checks are under "Sources & data notes". Figures are never converted between currencies or mixed between kinds.
          </p>
          <div className="mt-6"><CountryCoverage countries={d.countries} /></div>
        </div>
        <QSection id="graduates" n={1} title={`What do ${name} graduates earn?`} lede="Graduate outcomes: people who studied this subject, measured a set time after graduating.">
          <GraduatePanel g={d.graduate} sources={d.sources} />
        </QSection>
        <QSection id="occupations" n={2} title="What do related jobs pay?" lede="Occupation-wide pay for jobs this subject often leads to. This is everyone in the job, not new graduates.">
          <OccupationPanel d={d} />
        </QSection>
        <QSection id="loans" n={3} title="How do student loans work?" lede="Published rules for government student loans. No repayment estimate is calculated here.">
          <LoanPanel loans={d.loans} sources={d.sources} />
        </QSection>
        <QSection id="costs" n={4} title={`What does it cost to study ${name} in each country?`} lede="Tuition by fee status and official living-cost figures, country by country, in local currency. Not a ranking.">
          <CostsPanel costs={d.costs} sources={d.sources} />
        </QSection>
        <QSection id="definitions" n={5} title="What each kind of figure means" lede="Rules the guide follows for figures and employers.">
          <Definitions defs={d.definitions} employers={d.employers} />
        </QSection>
        <QSection id="sources" n={6} title="Sources, checks and gaps">
          <SourcesQa d={d} />
        </QSection>
      </div>
    </div>
  );
}
